/**
 * Client-side error reporting for whatever reaches {@link
 * import('./error-content').ErrorContent}.
 *
 * ## Why this exists
 *
 * `error.internal.description` in the catalogue tells the user "The issue has
 * been logged." Nothing on the client logged anything, so that sentence was not
 * true: every render-time failure and every fatal read collapsed to the same
 * six-word screen with no stack, no route and no correlation id anywhere a
 * developer could reach it. A user could only report "the calendar shows
 * *Something went wrong* until I reload", which is unactionable.
 *
 * `toErrorCode` maps *anything* that is not an {@link ApiRequestError} to
 * `internal`, so the generic screen covers two very different populations - a
 * real 5xx from the API, and any exception thrown while rendering. They are
 * indistinguishable to the user by design (R2.8/R3.6: no error shape may reveal
 * whether a withheld record exists), which is exactly why the *developer* needs
 * the untruncated detail written somewhere.
 *
 * ## What is recorded, and where
 *
 * `console.error`, with the route the failure happened on and the correlation id
 * when the API supplied one. The browser console keeps the stack and the source
 * map, which is what turns "something went wrong" into a file and a line.
 *
 * This is deliberately *not* a network call to a collector endpoint: no such
 * endpoint exists in the API surface, and inventing one would mean deciding what
 * of a planning screen's state is safe to transmit - a confidentiality question
 * (R3) that belongs in a spec, not in an error handler. When a collector is
 * added, it goes here and every call site already routes through it.
 */
import { ApiRequestError } from '@/shared/api/api-error';

/** What a reported failure carries beyond the error itself. */
export interface ErrorReportContext {
  /** Where the failure surfaced, e.g. `'route-boundary'` or `'session-bootstrap'`. */
  readonly boundary: string;
}

/**
 * The error's own name, message and leading stack frames, as displayable text.
 *
 * Rendered on the error screen in development builds only (see {@link
 * import('./error-content').ErrorContent}). The console entry {@link reportError}
 * writes is the better record - it keeps the live object and the source map - but
 * it is only useful to someone who thinks to open dev tools *before* navigating
 * away. A failure that reproduces on a colleague's machine and not on yours is
 * usually reported as a screenshot, and a screenshot of the generic six-word
 * screen carries no information at all. Putting the detail on screen means the
 * screenshot is the bug report.
 *
 * Never rendered in a production build: the taxonomy shapes are deliberately
 * uninformative so that no error can reveal whether a withheld record exists
 * (R2.8, R3.6), and a raw message or stack could carry an identifier, a title or
 * a query fragment straight through that guarantee.
 */
export function describeError(error: unknown): string | null {
  if (error instanceof Error) {
    const stack = (error.stack ?? '')
      .split('\n')
      .slice(1, 6)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .join('\n');
    const head = `${error.name}: ${error.message}`;
    return stack.length > 0 ? `${head}\n${stack}` : head;
  }
  if (typeof error === 'string' && error.length > 0) {
    return error;
  }
  // A router `Response` rejection (a loader's `throw new Response(...)`) and any
  // other non-`Error` throw still deserve *something* identifiable.
  if (typeof error === 'object' && error !== null) {
    try {
      return JSON.stringify(error).slice(0, 800);
    } catch {
      return Object.prototype.toString.call(error);
    }
  }
  return null;
}

/**
 * Records one failure for a developer.
 *
 * Idempotent per error object: {@link import('./error-content').ErrorContent}
 * calls this from an effect, and React may run an effect twice in development's
 * strict mode, so the same error must not produce two console entries. Tracked
 * on a `WeakSet` so a reported error is not retained once the tree that threw it
 * is collected.
 */
export function reportError(error: unknown, context: ErrorReportContext): void {
  if (typeof error === 'object' && error !== null) {
    if (reported.has(error)) return;
    reported.add(error);
  }

  // Read straight off `ApiRequestError` rather than through `error-content`'s
  // `correlationIdOf`, so this module does not import the module that imports it.
  const correlationId = error instanceof ApiRequestError ? error.body.correlationId : '';
  // One entry, so a user can be asked for a single screenshot of the console.
  // The error object is passed last and unwrapped, not stringified, so the
  // browser renders its real stack against the build's source map.
  console.error(
    `[ComPlan] ${context.boundary} failed at ${window.location.pathname}${window.location.search}` +
      (correlationId ? ` (correlation id ${correlationId})` : ''),
    error,
  );
}

const reported = new WeakSet<object>();
